import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../lib/supabase'
import { FIELD_LIMITS, truncateField } from '../../../utils/validation'

/**
 * User profile data
 */
interface UserProfile {
    email?: string
    full_name: string
    bio: string
    avatar_url: string | null
}

/**
 * Profile update fields
 */
interface ProfileUpdateData {
    full_name?: string
    bio?: string
}

/**
 * Operation result
 */
interface OperationResult {
    success: boolean
    error?: string
    email?: string
    url?: string
}

/**
 * Return type for useProfileSettings hook
 */
interface UseProfileSettingsReturn {
    profile: UserProfile | null
    isLoading: boolean
    loadProfile: () => Promise<void>
    updateProfile: (updates: ProfileUpdateData) => Promise<OperationResult>
    uploadAvatar: (file: File) => Promise<OperationResult>
    requestPasswordReset: () => Promise<OperationResult>
}

/**
 * Custom hook for managing user profile settings
 * 
 * Handles profile loading, updating, avatar upload, and password reset
 */
export function useProfileSettings(): UseProfileSettingsReturn {
    const [profile, setProfile] = useState<UserProfile | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    /**
     * Load user profile from Supabase Auth
     */
    const loadProfile = useCallback(async () => {
        try {
            setIsLoading(true)
            const {
                data: { user },
                error,
            } = await supabase.auth.getUser()

            if (error) throw error

            if (user) {
                setProfile({
                    email: user.email,
                    full_name: user.user_metadata?.full_name || '',
                    bio: user.user_metadata?.bio || '',
                    avatar_url: user.user_metadata?.avatar_url || null,
                })
            }
        } catch (error) {
            console.error('Error loading profile:', error)
            setProfile(null)
        } finally {
            setIsLoading(false)
        }
    }, [])

    /**
     * Update user profile (name and bio)
     */
    const updateProfile = useCallback(async (updates: ProfileUpdateData): Promise<OperationResult> => {
        try {
            setIsLoading(true)

            const truncatedData = {
                full_name: truncateField(updates.full_name, FIELD_LIMITS.profiles.full_name),
                bio: truncateField(updates.bio, FIELD_LIMITS.profiles.bio),
            }

            const { error } = await supabase.auth.updateUser({
                data: truncatedData,
            })

            if (error) throw error

            // Update local state
            setProfile((prev) =>
                prev
                    ? {
                        ...prev,
                        full_name: truncatedData.full_name || '',
                        bio: truncatedData.bio || '',
                    }
                    : null
            )

            return { success: true }
        } catch (error) {
            console.error('Error updating profile:', error)
            const message = error instanceof Error ? error.message : 'Unknown error'
            return { success: false, error: message }
        } finally {
            setIsLoading(false)
        }
    }, [])

    /**
     * Upload avatar image to Supabase Storage
     */
    const uploadAvatar = useCallback(async (file: File): Promise<OperationResult> => {
        try {
            setIsLoading(true)

            if (!file) {
                throw new Error('No file provided')
            }

            const fileExt = file.name.split('.').pop()
            const {
                data: { user },
            } = await supabase.auth.getUser()
            const fileName = `${user?.id || 'anon'}-${Date.now()}.${fileExt}`
            const filePath = `${fileName}`

            // Upload to Supabase Storage
            const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, file)

            if (uploadError) throw uploadError

            // Get Public URL
            const {
                data: { publicUrl },
            } = supabase.storage.from('avatars').getPublicUrl(filePath)

            // Update User Metadata
            const { error: updateError } = await supabase.auth.updateUser({
                data: { avatar_url: publicUrl },
            })

            if (updateError) throw updateError

            // Update local state
            setProfile((prev) =>
                prev
                    ? {
                        ...prev,
                        avatar_url: publicUrl,
                    }
                    : null
            )

            return { success: true, url: publicUrl }
        } catch (error) {
            console.error('Error uploading avatar:', error)
            return {
                success: false,
                error: 'Error al subir la imagen. Asegúrate de que el bucket "avatars" exista en Supabase y sea público.',
            }
        } finally {
            setIsLoading(false)
        }
    }, [])

    /**
     * Request password reset email
     */
    const requestPasswordReset = useCallback(async (): Promise<OperationResult> => {
        try {
            setIsLoading(true)

            if (!profile?.email) {
                throw new Error('No email found')
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const redirectUrl = (import.meta as any).env.VITE_AUTH_REDIRECT_URL || globalThis.location.origin

            const { error } = await supabase.auth.resetPasswordForEmail(profile.email, {
                redirectTo: redirectUrl,
            })

            if (error) throw error

            return { success: true, email: profile.email }
        } catch (error) {
            console.error('Error requesting password reset:', error)
            const message = error instanceof Error ? error.message : 'Unknown error'
            return { success: false, error: message }
        } finally {
            setIsLoading(false)
        }
    }, [profile?.email])

    // Auto-load profile on mount
    useEffect(() => {
        loadProfile()
    }, [loadProfile])

    return {
        profile,
        isLoading,
        loadProfile,
        updateProfile,
        uploadAvatar,
        requestPasswordReset,
    }
}
